/**
 * MyCIO referral / problem-template growth-loop copy catalog.
 *
 * Two surfaces share this dictionary so both render in the viewer's language:
 *  - `template`: the /instant "send someone the problem-type template" affordance (Loop C, a GENERIC
 *    problem-type starter — never the user's private numbers, NO dollar amounts).
 *  - `status`: the dashboard referral-status + reward-disclosure card.
 *
 * `en` is byte-identical to the strings the surfaces shipped with. zh-Hant is auto-derived from `zh` by
 * pickCopy (opencc cn→tw) — do not hand-author it; the 13 non-en locales below are authored.
 *
 * COMPLIANCE / register: computed, not advice — never "recommendation"/"advice" language. Band-only, never
 * user dollar amounts in any shared text. A reward is disclosed ONLY when the reward engine is live
 * (`terms.enabled`); when it is off, the neutral invite line makes NO incentive claim.
 */
import { pickCopy } from '@/lib/mycio/translations'
import { getMyCIOLanguage, type MyCIOLanguageCode } from '@/lib/mycio/language'

export type MyCIOReferralShareCopy = {
  template: {
    prompt: (problemType: string) => string
    genericProblem: string
    cta: string
    sent: string
    copied: string
    note: string
    shareTitle: string
    shareText: (problemType: string) => string
  }
  status: {
    title: string
    invited: (n: number) => string
    converted: (n: number) => string
    earnedCredit: (usd: number) => string
    reward: (creditUsd: number, trialMonths: number) => string
    neutralInvite: string
    share: string
    sent: string
    copied: string
    shareTitle: string
    shareText: string
    loading: string
    error: string
  }
}

const en: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `Know someone weighing ${p}? Send them the template.`,
    genericProblem: 'this decision',
    cta: 'Send the template',
    sent: 'Shared',
    copied: 'Link copied',
    note: 'A generic starter — none of your numbers.',
    shareTitle: 'A MyCIO starter template',
    shareText: (p) => `Here's a MyCIO template for ${p} — mostly set up for your situation. Run your own numbers in one sentence. Computed estimates, not advice.`,
  },
  status: {
    title: 'Invite a friend',
    invited: (n) => `${n} ${n === 1 ? 'friend' : 'friends'} invited`,
    converted: (n) => `${n} joined & subscribed`,
    earnedCredit: (usd) => `$${usd} in credit earned`,
    reward: (creditUsd, trialMonths) => `Invite a friend — they get a ${trialMonths}-month Living trial, and you get $${creditUsd} toward your next renewal when they subscribe.`,
    neutralInvite: 'Invite a friend to MyCIO.',
    share: 'Invite a friend',
    sent: 'Shared',
    copied: 'Link copied',
    shareTitle: 'MyCIO',
    shareText: 'I ran my numbers on MyCIO in one sentence — computed estimates, not advice. Try yours.',
    loading: 'Loading…',
    error: "Couldn't load your invites right now.",
  },
}

const es: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `¿Conoces a alguien que esté pensando en ${p}? Envíale la plantilla.`,
    genericProblem: 'esta decisión',
    cta: 'Enviar la plantilla',
    sent: 'Compartido',
    copied: 'Enlace copiado',
    note: 'Una plantilla genérica — ninguno de tus números.',
    shareTitle: 'Una plantilla inicial de MyCIO',
    shareText: (p) => `Aquí tienes una plantilla de MyCIO para ${p} — casi lista para tu situación. Calcula tus propios números en una frase. Estimaciones calculadas, no asesoramiento.`,
  },
  status: {
    title: 'Invita a un amigo',
    invited: (n) => `${n} ${n === 1 ? 'amigo invitado' : 'amigos invitados'}`,
    converted: (n) => `${n} se ${n === 1 ? 'unió' : 'unieron'} y se suscribieron`,
    earnedCredit: (usd) => `$${usd} en crédito ganado`,
    reward: (creditUsd, trialMonths) => `Invita a un amigo — recibe una prueba Living de ${trialMonths} mes(es), y tú recibes $${creditUsd} para tu próxima renovación cuando se suscriba.`,
    neutralInvite: 'Invita a un amigo a MyCIO.',
    share: 'Invita a un amigo',
    sent: 'Compartido',
    copied: 'Enlace copiado',
    shareTitle: 'MyCIO',
    shareText: 'Calculé mis números en MyCIO en una frase — estimaciones calculadas, no asesoramiento. Prueba los tuyos.',
    loading: 'Cargando…',
    error: 'No se pudieron cargar tus invitaciones ahora mismo.',
  },
}

const pt: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `Conhece alguém pensando em ${p}? Envie o modelo.`,
    genericProblem: 'esta decisão',
    cta: 'Enviar o modelo',
    sent: 'Compartilhado',
    copied: 'Link copiado',
    note: 'Um modelo genérico — nenhum dos seus números.',
    shareTitle: 'Um modelo inicial do MyCIO',
    shareText: (p) => `Aqui está um modelo do MyCIO para ${p} — quase pronto para a sua situação. Calcule seus próprios números em uma frase. Estimativas calculadas, não aconselhamento.`,
  },
  status: {
    title: 'Convide um amigo',
    invited: (n) => `${n} ${n === 1 ? 'amigo convidado' : 'amigos convidados'}`,
    converted: (n) => `${n} ${n === 1 ? 'entrou e assinou' : 'entraram e assinaram'}`,
    earnedCredit: (usd) => `$${usd} em crédito ganho`,
    reward: (creditUsd, trialMonths) => `Convide um amigo — ele ganha um teste Living de ${trialMonths} mês(es), e você ganha $${creditUsd} para a sua próxima renovação quando ele assinar.`,
    neutralInvite: 'Convide um amigo para o MyCIO.',
    share: 'Convide um amigo',
    sent: 'Compartilhado',
    copied: 'Link copiado',
    shareTitle: 'MyCIO',
    shareText: 'Calculei meus números no MyCIO em uma frase — estimativas calculadas, não aconselhamento. Experimente os seus.',
    loading: 'Carregando…',
    error: 'Não foi possível carregar seus convites agora.',
  },
}

const fr: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `Vous connaissez quelqu’un qui pèse ${p} ? Envoyez-lui le modèle.`,
    genericProblem: 'cette décision',
    cta: 'Envoyer le modèle',
    sent: 'Partagé',
    copied: 'Lien copié',
    note: 'Un modèle générique — aucun de vos chiffres.',
    shareTitle: 'Un modèle de départ MyCIO',
    shareText: (p) => `Voici un modèle MyCIO pour ${p} — presque prêt pour votre situation. Calculez vos propres chiffres en une phrase. Estimations calculées, pas un conseil.`,
  },
  status: {
    title: 'Invitez un ami',
    invited: (n) => `${n} ${n === 1 ? 'ami invité' : 'amis invités'}`,
    converted: (n) => `${n} ${n === 1 ? 'inscrit et abonné' : 'inscrits et abonnés'}`,
    earnedCredit: (usd) => `$${usd} de crédit gagné`,
    reward: (creditUsd, trialMonths) => `Invitez un ami — il reçoit un essai Living de ${trialMonths} mois, et vous recevez $${creditUsd} sur votre prochain renouvellement lorsqu’il s’abonne.`,
    neutralInvite: 'Invitez un ami sur MyCIO.',
    share: 'Invitez un ami',
    sent: 'Partagé',
    copied: 'Lien copié',
    shareTitle: 'MyCIO',
    shareText: 'J’ai calculé mes chiffres sur MyCIO en une phrase — estimations calculées, pas un conseil. Essayez les vôtres.',
    loading: 'Chargement…',
    error: 'Impossible de charger vos invitations pour le moment.',
  },
}

const de: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `Kennen Sie jemanden, der über ${p} nachdenkt? Schicken Sie die Vorlage.`,
    genericProblem: 'diese Entscheidung',
    cta: 'Vorlage senden',
    sent: 'Geteilt',
    copied: 'Link kopiert',
    note: 'Eine allgemeine Vorlage — keine Ihrer Zahlen.',
    shareTitle: 'Eine MyCIO-Startvorlage',
    shareText: (p) => `Hier ist eine MyCIO-Vorlage für ${p} — weitgehend auf Ihre Situation eingerichtet. Rechnen Sie Ihre eigenen Zahlen in einem Satz. Berechnete Schätzungen, keine Beratung.`,
  },
  status: {
    title: 'Freund einladen',
    invited: (n) => `${n} ${n === 1 ? 'Freund eingeladen' : 'Freunde eingeladen'}`,
    converted: (n) => `${n} beigetreten & abonniert`,
    earnedCredit: (usd) => `$${usd} Guthaben erhalten`,
    reward: (creditUsd, trialMonths) => `Laden Sie einen Freund ein — er erhält eine ${trialMonths}-monatige Living-Testphase, und Sie erhalten $${creditUsd} für Ihre nächste Verlängerung, wenn er abonniert.`,
    neutralInvite: 'Laden Sie einen Freund zu MyCIO ein.',
    share: 'Freund einladen',
    sent: 'Geteilt',
    copied: 'Link kopiert',
    shareTitle: 'MyCIO',
    shareText: 'Ich habe meine Zahlen bei MyCIO in einem Satz gerechnet — berechnete Schätzungen, keine Beratung. Rechnen Sie Ihre.',
    loading: 'Wird geladen…',
    error: 'Ihre Einladungen konnten gerade nicht geladen werden.',
  },
}

const it: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `Conosci qualcuno che sta valutando ${p}? Mandagli il modello.`,
    genericProblem: 'questa decisione',
    cta: 'Invia il modello',
    sent: 'Condiviso',
    copied: 'Link copiato',
    note: 'Un modello generico — nessuno dei tuoi numeri.',
    shareTitle: 'Un modello iniziale MyCIO',
    shareText: (p) => `Ecco un modello MyCIO per ${p} — quasi pronto per la tua situazione. Calcola i tuoi numeri in una frase. Stime calcolate, non consulenza.`,
  },
  status: {
    title: 'Invita un amico',
    invited: (n) => `${n} ${n === 1 ? 'amico invitato' : 'amici invitati'}`,
    converted: (n) => `${n} ${n === 1 ? 'iscritto e abbonato' : 'iscritti e abbonati'}`,
    earnedCredit: (usd) => `$${usd} di credito guadagnato`,
    reward: (creditUsd, trialMonths) => `Invita un amico — riceve una prova Living di ${trialMonths} mese(i), e tu ricevi $${creditUsd} sul tuo prossimo rinnovo quando si abbona.`,
    neutralInvite: 'Invita un amico su MyCIO.',
    share: 'Invita un amico',
    sent: 'Condiviso',
    copied: 'Link copiato',
    shareTitle: 'MyCIO',
    shareText: 'Ho calcolato i miei numeri su MyCIO in una frase — stime calcolate, non consulenza. Prova i tuoi.',
    loading: 'Caricamento…',
    error: 'Impossibile caricare i tuoi inviti in questo momento.',
  },
}

const ru: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `Знаете кого-то, кто раздумывает над «${p}»? Отправьте ему шаблон.`,
    genericProblem: 'это решение',
    cta: 'Отправить шаблон',
    sent: 'Отправлено',
    copied: 'Ссылка скопирована',
    note: 'Общий шаблон — ни одной вашей цифры.',
    shareTitle: 'Стартовый шаблон MyCIO',
    shareText: (p) => `Вот шаблон MyCIO для «${p}» — почти настроен под вашу ситуацию. Посчитайте свои цифры одной фразой. Расчётные оценки, не рекомендация.`,
  },
  status: {
    title: 'Пригласить друга',
    invited: (n) => `Приглашено друзей: ${n}`,
    converted: (n) => `Присоединились и оформили подписку: ${n}`,
    earnedCredit: (usd) => `Начислено кредита: $${usd}`,
    reward: (creditUsd, trialMonths) => `Пригласите друга — он получит пробный период Living на ${trialMonths} мес., а вы получите $${creditUsd} к следующему продлению, когда он оформит подписку.`,
    neutralInvite: 'Пригласите друга в MyCIO.',
    share: 'Пригласить друга',
    sent: 'Отправлено',
    copied: 'Ссылка скопирована',
    shareTitle: 'MyCIO',
    shareText: 'Я посчитал свои цифры в MyCIO одной фразой — расчётные оценки, не рекомендация. Попробуйте свои.',
    loading: 'Загрузка…',
    error: 'Не удалось загрузить ваши приглашения прямо сейчас.',
  },
}

const zh: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `认识正在考虑${p}的人吗？把模板发给他们。`,
    genericProblem: '这个决定',
    cta: '发送模板',
    sent: '已分享',
    copied: '链接已复制',
    note: '一个通用模板——不含你的任何数字。',
    shareTitle: 'MyCIO 入门模板',
    shareText: (p) => `这是一个用于${p}的 MyCIO 模板——已大致按你的情况设好。用一句话算出你自己的数字。计算估算，非建议。`,
  },
  status: {
    title: '邀请好友',
    invited: (n) => `已邀请 ${n} 位好友`,
    converted: (n) => `${n} 位已加入并订阅`,
    earnedCredit: (usd) => `已获得 $${usd} 抵扣额度`,
    reward: (creditUsd, trialMonths) => `邀请一位好友——他可获得 ${trialMonths} 个月的 Living 试用，当他订阅后，你可获得 $${creditUsd} 用于下次续费。`,
    neutralInvite: '邀请好友加入 MyCIO。',
    share: '邀请好友',
    sent: '已分享',
    copied: '链接已复制',
    shareTitle: 'MyCIO',
    shareText: '我在 MyCIO 上用一句话算出了我的数字——计算估算，非建议。来试试你的。',
    loading: '加载中…',
    error: '暂时无法加载你的邀请。',
  },
}

const ja: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `${p}を検討している人はいませんか？テンプレートを送りましょう。`,
    genericProblem: 'この判断',
    cta: 'テンプレートを送る',
    sent: '共有しました',
    copied: 'リンクをコピーしました',
    note: '汎用テンプレートです — あなたの数字は含みません。',
    shareTitle: 'MyCIO のスターターテンプレート',
    shareText: (p) => `${p}向けの MyCIO テンプレートです — あなたの状況にほぼ合わせて設定済み。ひと言であなた自身の数字を計算できます。計算による試算であり、助言ではありません。`,
  },
  status: {
    title: '友達を招待',
    invited: (n) => `${n} 人を招待済み`,
    converted: (n) => `${n} 人が参加して定期購入`,
    earnedCredit: (usd) => `$${usd} 分のクレジットを獲得しました`,
    reward: (creditUsd, trialMonths) => `友達を招待しましょう — 友達は ${trialMonths} か月の Living トライアルを受け取り、友達が定期購入すると、あなたは次回の更新に使える $${creditUsd} を受け取れます。`,
    neutralInvite: '友達を MyCIO に招待しましょう。',
    share: '友達を招待',
    sent: '共有しました',
    copied: 'リンクをコピーしました',
    shareTitle: 'MyCIO',
    shareText: 'MyCIO でひと言で自分の数字を計算しました — 計算による試算であり、助言ではありません。あなたも試してみて。',
    loading: '読み込み中…',
    error: '現在、招待を読み込めませんでした。',
  },
}

const ko: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `${p}을(를) 고민하는 사람이 있나요? 템플릿을 보내주세요.`,
    genericProblem: '이 결정',
    cta: '템플릿 보내기',
    sent: '공유됨',
    copied: '링크 복사됨',
    note: '일반 템플릿입니다 — 당신의 숫자는 없습니다.',
    shareTitle: 'MyCIO 시작 템플릿',
    shareText: (p) => `${p}을(를) 위한 MyCIO 템플릿입니다 — 상황에 맞게 대부분 설정되어 있어요. 한 문장으로 당신의 숫자를 계산해 보세요. 계산된 추정치이며 조언이 아닙니다.`,
  },
  status: {
    title: '친구 초대',
    invited: (n) => `친구 ${n}명 초대함`,
    converted: (n) => `${n}명 가입 및 구독함`,
    earnedCredit: (usd) => `$${usd} 크레딧을 받았습니다`,
    reward: (creditUsd, trialMonths) => `친구를 초대하세요 — 친구는 ${trialMonths}개월 Living 체험을 받고, 친구가 구독하면 다음 갱신에 사용할 수 있는 $${creditUsd}를 받습니다.`,
    neutralInvite: '친구를 MyCIO에 초대하세요.',
    share: '친구 초대',
    sent: '공유됨',
    copied: '링크 복사됨',
    shareTitle: 'MyCIO',
    shareText: 'MyCIO에서 한 문장으로 제 숫자를 계산했어요 — 계산된 추정치이며 조언이 아닙니다. 당신도 해보세요.',
    loading: '불러오는 중…',
    error: '지금은 초대를 불러올 수 없습니다.',
  },
}

const ar: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `أتعرف شخصًا يفكر في ${p}؟ أرسل له القالب.`,
    genericProblem: 'هذا القرار',
    cta: 'إرسال القالب',
    sent: 'تمت المشاركة',
    copied: 'تم نسخ الرابط',
    note: 'قالب عام — لا يتضمن أيًّا من أرقامك.',
    shareTitle: 'قالب انطلاق من MyCIO',
    shareText: (p) => `إليك قالب MyCIO لـ${p} — مُعدّ إلى حد كبير لحالتك. احسب أرقامك بجملة واحدة. تقديرات محسوبة، وليست نصيحة.`,
  },
  status: {
    title: 'ادعُ صديقًا',
    invited: (n) => `تمت دعوة ${n} من الأصدقاء`,
    converted: (n) => `انضم ${n} واشترك`,
    earnedCredit: (usd) => `ربحت رصيدًا بقيمة $${usd}`,
    reward: (creditUsd, trialMonths) => `ادعُ صديقًا — يحصل على تجربة Living لمدة ${trialMonths} شهر، وتحصل أنت على $${creditUsd} لتجديدك القادم عندما يشترك.`,
    neutralInvite: 'ادعُ صديقًا إلى MyCIO.',
    share: 'ادعُ صديقًا',
    sent: 'تمت المشاركة',
    copied: 'تم نسخ الرابط',
    shareTitle: 'MyCIO',
    shareText: 'حسبت أرقامي على MyCIO بجملة واحدة — تقديرات محسوبة، وليست نصيحة. جرّب أرقامك.',
    loading: 'جارٍ التحميل…',
    error: 'تعذّر تحميل دعواتك الآن.',
  },
}

const hi: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `किसी को ${p} पर विचार करते जानते हैं? उन्हें टेम्पलेट भेजें।`,
    genericProblem: 'यह निर्णय',
    cta: 'टेम्पलेट भेजें',
    sent: 'साझा किया',
    copied: 'लिंक कॉपी हुआ',
    note: 'एक सामान्य टेम्पलेट — इसमें आपका कोई आँकड़ा नहीं है।',
    shareTitle: 'MyCIO का शुरुआती टेम्पलेट',
    shareText: (p) => `${p} के लिए यह MyCIO टेम्पलेट है — काफी हद तक आपकी स्थिति के अनुसार तैयार। एक वाक्य में अपने आँकड़े निकालें। गणना किए गए अनुमान, सलाह नहीं।`,
  },
  status: {
    title: 'किसी मित्र को आमंत्रित करें',
    invited: (n) => `${n} ${n === 1 ? 'मित्र' : 'मित्र'} आमंत्रित`,
    converted: (n) => `${n} शामिल होकर सदस्यता ली`,
    earnedCredit: (usd) => `$${usd} का क्रेडिट अर्जित किया`,
    reward: (creditUsd, trialMonths) => `किसी मित्र को आमंत्रित करें — उसे ${trialMonths} माह का Living ट्रायल मिलता है, और जब वह सदस्यता लेता है तो आपको अपने अगले नवीनीकरण के लिए $${creditUsd} मिलते हैं।`,
    neutralInvite: 'किसी मित्र को MyCIO पर आमंत्रित करें।',
    share: 'किसी मित्र को आमंत्रित करें',
    sent: 'साझा किया',
    copied: 'लिंक कॉपी हुआ',
    shareTitle: 'MyCIO',
    shareText: 'मैंने MyCIO पर एक वाक्य में अपने आँकड़े निकाले — गणना किए गए अनुमान, सलाह नहीं। आप भी आज़माएँ।',
    loading: 'लोड हो रहा है…',
    error: 'अभी आपके आमंत्रण लोड नहीं हो सके।',
  },
}

const id: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `Kenal seseorang yang sedang mempertimbangkan ${p}? Kirimkan templatnya.`,
    genericProblem: 'keputusan ini',
    cta: 'Kirim templat',
    sent: 'Dibagikan',
    copied: 'Tautan disalin',
    note: 'Templat umum — tanpa angka Anda sama sekali.',
    shareTitle: 'Templat awal MyCIO',
    shareText: (p) => `Ini templat MyCIO untuk ${p} — sebagian besar sudah disiapkan untuk situasi Anda. Hitung angka Anda sendiri dalam satu kalimat. Estimasi terhitung, bukan nasihat.`,
  },
  status: {
    title: 'Undang teman',
    invited: (n) => `${n} teman diundang`,
    converted: (n) => `${n} bergabung & berlangganan`,
    earnedCredit: (usd) => `$${usd} kredit diperoleh`,
    reward: (creditUsd, trialMonths) => `Undang teman — dia mendapat uji coba Living ${trialMonths} bulan, dan Anda mendapat $${creditUsd} untuk perpanjangan berikutnya saat dia berlangganan.`,
    neutralInvite: 'Undang teman ke MyCIO.',
    share: 'Undang teman',
    sent: 'Dibagikan',
    copied: 'Tautan disalin',
    shareTitle: 'MyCIO',
    shareText: 'Saya menghitung angka saya di MyCIO dalam satu kalimat — estimasi terhitung, bukan nasihat. Coba milik Anda.',
    loading: 'Memuat…',
    error: 'Tidak dapat memuat undangan Anda saat ini.',
  },
}

const tr: MyCIOReferralShareCopy = {
  template: {
    prompt: (p) => `${p} konusunu değerlendiren birini tanıyor musun? Şablonu ona gönder.`,
    genericProblem: 'bu karar',
    cta: 'Şablonu gönder',
    sent: 'Paylaşıldı',
    copied: 'Bağlantı kopyalandı',
    note: 'Genel bir şablon — senin hiçbir rakamın yok.',
    shareTitle: 'MyCIO başlangıç şablonu',
    shareText: (p) => `${p} için bir MyCIO şablonu — büyük ölçüde durumuna göre hazırlandı. Kendi rakamlarını tek cümleyle hesapla. Hesaplanmış tahminler, tavsiye değil.`,
  },
  status: {
    title: 'Bir arkadaşını davet et',
    invited: (n) => `${n} arkadaş davet edildi`,
    converted: (n) => `${n} katıldı ve abone oldu`,
    earnedCredit: (usd) => `$${usd} kredi kazandın`,
    reward: (creditUsd, trialMonths) => `Bir arkadaşını davet et — o ${trialMonths} aylık Living denemesi alır, o abone olduğunda sen de sonraki yenilemende kullanmak üzere $${creditUsd} alırsın.`,
    neutralInvite: 'Bir arkadaşını MyCIO’ya davet et.',
    share: 'Bir arkadaşını davet et',
    sent: 'Paylaşıldı',
    copied: 'Bağlantı kopyalandı',
    shareTitle: 'MyCIO',
    shareText: 'Rakamlarımı MyCIO’da tek cümleyle hesapladım — hesaplanmış tahminler, tavsiye değil. Seninkileri dene.',
    loading: 'Yükleniyor…',
    error: 'Davetlerin şu anda yüklenemedi.',
  },
}

const referralShareByLanguage: Partial<Record<MyCIOLanguageCode, MyCIOReferralShareCopy>> = {
  en, es, pt, fr, de, it, ru, zh, ja, ko, ar, hi, id, tr,
}

export function getMyCIOReferralShareCopy(language: string | null | undefined): MyCIOReferralShareCopy {
  const code = getMyCIOLanguage(language).code
  return pickCopy(code, referralShareByLanguage, en)
}
